import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const difficultyLabel = {
  E: "Easy",
  SE: "Slightly Easy",
  M: "Medium",
  T: "Tough",
};

const ResultPage = () => {
  const { attemptId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const printableRef = useRef(null);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const { data } = await axios.get(
          `https://doshfebackend.teemah.com.ng/api/assessments/attempts/${attemptId}/result/`
        );
        setResult(data);
      } catch (error) {
        console.error("Error fetching result:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [attemptId]);

  const handleDownload = () => {
    const input = printableRef.current;
    
    // Add special print styles temporarily
    const printStyles = document.createElement('style');
    printStyles.innerHTML = `
      @media print {
        body, html { width: 100% !important; height: auto !important; }
        .print-container { box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
        .no-print { display: none !important; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `;
    document.head.appendChild(printStyles);

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    }).then(canvas => {
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const filenameSafe = (result?.student_name || "result")
        .replace(/\s+/g, "_")
        .replace(/[^\w-]/g, "");
      
      pdf.save(`${filenameSafe}_assessment_result.pdf`);
      
      // Clean up
      document.head.removeChild(printStyles);
    }).catch(error => {
      console.error('PDF generation error:', error);
      alert("Unable to generate PDF. Please try again.");
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-lg font-medium text-gray-600 animate-pulse">
          Loading result...
        </p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-lg font-medium text-red-500">❌ No result found.</p>
      </div>
    );
  }

  const overallPctNum = Number(result.percentage ?? 0);
  const overallPctText = overallPctNum.toFixed(2);
  const ringColor =
    overallPctNum >= 70 ? "#16a34a" : overallPctNum >= 50 ? "#ca8a04" : "#dc2626";

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const analysisEntries = Object.entries(result.analysis || {});

  return (
    <div className="min-h-screen bg-gray-100 px-3 py-8 md:px-6 ">
      <div className="max-w-4xl mx-auto mt-20">
        {/* Printable Area */}
        <div
          ref={printableRef}
          className="w-full bg-white shadow-xl rounded-2xl overflow-hidden print-container"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-yellow-100 via-yellow-200 to-yellow-300 p-4 md:p-8 text-gray-800  overflow-hidden">
            <div className="absolute inset-0 bg-yellow-400/20 blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Logo + Title */}
              <div className="flex items-center gap-3 md:gap-4">
                <img
                  src="/dca.png"
                  alt="Dosh Learning Hub"
                  className="h-20 w-20 md:h-20 md:w-20  border-yellow-500 drop-shadow-lg object-contain"
                />
                <div>
                  <h1 className="text-sm md:text-lg font-semibold tracking-wide uppercase text-yellow-800/90">
                    Free Maths Assessment for{" "}
                    <span className="text-yellow-700">{result.class_student}</span>
                  </h1>
                  <p className="text-xl md:text-4xl font-bold mt-1 text-gray-900">
                    <span className="text-yellow-700 font-medium">Name:</span>{" "}
                    {result.student_name}
                  </p>
                </div>
              </div>
              {/* Motto */}
              <div className="md:text-right italic text-xs md:text-base text-yellow-900/80">
                <span className="border-l-4 border-yellow-600 pl-2 md:pl-3">
                  Excellence Through Learning ✨
                </span>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="p-4 md:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 text-gray-700">
              <div className="bg-white border rounded-lg md:rounded-xl p-3 md:p-4 shadow-sm">
                <p className="font-semibold text-sm md:text-base">Class</p>
                <p className="text-gray-600 text-sm md:text-base">{result.class_student}</p>
              </div>
              <div className="bg-white border rounded-lg md:rounded-xl p-3 md:p-4 shadow-sm">
                <p className="font-semibold text-sm md:text-base">Age</p>
                <p className="text-gray-600 text-sm md:text-base">{result.age}</p>
              </div>
              <div className="bg-white border rounded-lg md:rounded-xl p-3 md:p-4 shadow-sm">
                <p className="font-semibold text-sm md:text-base">Started</p>
                <p className="text-gray-600 text-xs md:text-sm">{formatDate(result.started_at)}</p>
              </div>
              <div className="bg-white border rounded-lg md:rounded-xl p-3 md:p-4 shadow-sm">
                <p className="font-semibold text-sm md:text-base">Completed</p>
                <p className="text-gray-600 text-xs md:text-sm">{formatDate(result.completed_at)}</p>
              </div>
              <div className="bg-white border rounded-lg md:rounded-xl p-3 md:p-4 shadow-sm sm:col-span-2">
                <p className="font-semibold text-sm md:text-base">Duration</p>
                <p className="text-gray-600 text-sm md:text-base">{result.duration_spent}</p>
              </div>
            </div>

            {/* Overall Score Gauge + Feedback */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-center">
              <div className="md:col-span-1 flex justify-center">
                {/* Circular Gauge */}
                <div className="relative h-28 w-28 md:h-36 md:w-36">
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    <circle
                      className="text-gray-200 stroke-current"
                      strokeWidth="10"
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                    />
                    <circle
                      className="stroke-current"
                      strokeWidth="10"
                      strokeLinecap="round"
                      cx="50"
                      cy="50"
                      r="40"
                      fill="transparent"
                      strokeDasharray="251.2"
                      strokeDashoffset={251.2 * (1 - overallPctNum / 100)}
                      style={{ stroke: ringColor }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <div className="text-lg md:text-xl font-bold">{overallPctText}%</div>
                    <div className="text-xs text-gray-500">Overall</div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                {/* Badge + backend feedback */}
                <div className="flex flex-wrap items-center gap-2 md:gap-3">
                  <span
                    className={`inline-block px-3 py-1 md:px-4 md:py-2 text-xs md:text-sm font-semibold rounded-full shadow-md ${
                      overallPctNum >= 50
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    Score: {overallPctText}%
                  </span>
                  {result.feedback && (
                    <span className="inline-block px-2 py-1 text-xs md:text-sm rounded-md bg-blue-50 text-blue-700 border border-blue-200">
                      {result.feedback}
                    </span>
                  )}
                </div>
                {/* Quick sentence */}
                <p className="mt-2 md:mt-3 text-gray-700 text-sm md:text-base">
                  Total Score: <span className="font-semibold">{result.total_score}</span>
                </p>
              </div>
            </div>

            {/* Analysis Table with Progress Bars */}
            {analysisEntries.length > 0 && (
              <div className="mt-6 md:mt-8">
                <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-3">
                  📊 Performance Analysis
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-200 rounded-lg shadow-sm">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="p-2 md:p-3 border text-left text-xs md:text-sm">Section</th>
                        <th className="p-2 md:p-3 border text-xs md:text-sm">Difficulty</th>
                        <th className="p-2 md:p-3 border text-xs md:text-sm">Correct</th>
                        <th className="p-2 md:p-3 border text-xs md:text-sm">Total</th>
                        <th className="p-2 md:p-3 border text-xs md:text-sm w-32 md:w-48">Progress</th>
                        <th className="p-2 md:p-3 border text-xs md:text-sm">Feedback</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisEntries.map(([sectionKey, stats]) => {
                        const pct = Number(stats.percentage || 0);
                        const barColor =
                          pct >= 70 ? "bg-green-600" : pct >= 50 ? "bg-yellow-500" : "bg-red-600";
                        return (
                          <tr key={sectionKey} className="text-center">
                            <td className="p-2 md:p-3 border text-left font-semibold text-xs md:text-sm">
                              {stats.section}
                            </td>
                            <td className="p-2 md:p-3 border text-xs md:text-sm">
                              <span className="px-1 py-0.5 md:px-2 md:py-1 text-xs rounded-md bg-gray-100">
                                {difficultyLabel[stats.difficulty] || stats.difficulty}
                              </span>
                            </td>
                            <td className="p-2 md:p-3 border text-green-700 font-medium text-xs md:text-sm">
                              {stats.correct}
                            </td>
                            <td className="p-2 md:p-3 border text-xs md:text-sm">{stats.total}</td>
                            <td className="p-2 md:p-3 border">
                              <div className="w-full bg-gray-200 rounded-full h-2 md:h-2.5 mx-auto">
                                <div
                                  className={`h-2 md:h-2.5 rounded-full ${barColor}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <div className="text-xs text-gray-600 mt-1">{pct}%</div>
                            </td>
                            <td className="p-2 md:p-3 border text-xs md:text-sm text-gray-600">
                              {stats.tailored_feedback}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Conclusion & Recommendation (from backend) */}
            <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className="bg-white border rounded-lg md:rounded-xl p-4 md:p-6 shadow-sm">
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">📝 Conclusion</h3>
                <p className="text-gray-700 text-sm md:text-base">{result.conclusion}</p>
              </div>
              <div className="bg-white border rounded-lg md:rounded-xl p-4 md:p-6 shadow-sm">
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">🎯 Recommendation</h3>
                <p className="text-gray-700 text-sm md:text-base">{result.recommendation}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Download Button */}
        <div className="mt-6 flex justify-center no-print">
          <button
            onClick={handleDownload}
            className="px-4 py-2 md:px-5 md:py-3 rounded-lg md:rounded-xl bg-blue-600 text-white font-semibold shadow-lg hover:bg-blue-700 active:scale-[0.99] transition text-sm md:text-base flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
            </svg>
            Download Result (PDF)
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultPage;